import { useEffect, useState } from 'react';
import config from '../config';
import { getLeafletZipsLayerClass } from '../utils/LeafletZipsLayer';

export default function useMapLayer({
  map, // Leaflet map
  mode, // "hover" | "touch"
  featuresById, // Map<string, boolean>
  layerParams, // idAttr, mapId, layerId, style, hoveredStyle, minZoom, maxZoom
  selectedFeatures, // Record<id, unknown>
  hoveredFeatureId, // number
  onHoverFeature, // (id: string) => void
  onSelectFeature, // (id: string) => void
}) {
  const [layer, setLayer] = useState(null);

  useEffect(() => {
    if (!map || !featuresById.size) {
      return;
    }

    const LayerClass = getLeafletZipsLayerClass({
      idAttr: layerParams.idAttr,
      url: `https://api.mapbox.com/v4/${layerParams.mapId}/{z}/{x}/{y}.mvt?access_token=${config.mapboxAccessToken}`,
      layerId: layerParams.layerId,
      style: layerParams.style,
      styleHovered: layerParams.styleHovered,
    });

    let f;
    const layer = new LayerClass({
      ...(layerParams.minZoom && { minZoom: layerParams.minZoom }),
      ...(layerParams.maxZoom && { maxZoom: layerParams.maxZoom }),
      hoveredLayers: mode === 'hover' ? null : [],
      onHover: function (event) {
        if (mode === 'touch') {
          return;
        }
        f = event.feature;
        if (f) {
          layer.setHoveredFeature(f.properties[layerParams.idAttr]);
          onHoverFeature(f.properties[layerParams.idAttr]);
        } else {
          layer.setHoveredFeature(null);
          onHoverFeature(null);
        }
      },
      filter: (feature) =>
        featuresById.has(feature.properties[layerParams.idAttr]),
    });

    map.addLayer(layer);

    setLayer(layer);

    return () => {
      map.removeLayer(layer);
    };
  }, [layerParams, map, mode, featuresById, onHoverFeature]);

  useEffect(() => {
    layer &&
      layer.updateOptions({
        onClick: function (event) {
          const id = event.feature?.properties[layerParams.idAttr];
          if (mode === 'hover') {
            id && onSelectFeature(id);
          } else {
            onHoverFeature(id);
          }
        },
      });
  }, [layerParams.idAttr, mode, layer, onSelectFeature, onHoverFeature]);

  useEffect(() => {
    if (!layer) {
      return;
    }

    const update = () => {
      layer.forEachFeature((f) => {
        f.toggleEnabled = false;
      });
      layer.setHoveredFeature(hoveredFeatureId);
      selectedFeatures &&
        layer.updateSelectedFeatures(
          Object.keys(selectedFeatures).reduce((res, key) => {
            res[key] = true;
            return res;
          }, {}),
        );
    };

    layer.updateOptions({ onTilesLoaded: update });
    update();
  }, [layer, selectedFeatures, hoveredFeatureId]);
}
